import {
  IEfaToFareRelevantJourneyPart,
  IEfaToFareVia,
} from '../journey-planning/efa-to-fare.interfaces';
import {
  IJourneyLeg,
  IJourneyLocation,
  IJourneyTrip,
} from '../journey-planning/journey-planning-types.interfaces';
import { ITariffRelationVia } from '../tariff/relation.interfaces';

export function resolveFareRelevantJourneyPart(
  trip: IJourneyTrip,
  onDemandLeg: IJourneyLeg,
  tariffSegment?: IEfaToFareVia
): IEfaToFareRelevantJourneyPart | undefined {
  if (!tariffSegment) {
    const regularLeg: IJourneyLeg | undefined = trip.legs.find(
      (leg: IJourneyLeg) =>
        !!leg.operator.id && leg.operator.id !== onDemandLeg.operator.id
    );

    return regularLeg
      ? {
          departureLeg: regularLeg,
          origin: regularLeg.origin.locationReferenceId,
          destination: regularLeg.destination.locationReferenceId,
        }
      : undefined;
  }

  const regularLegs: IJourneyLeg[] = trip.legs.filter(
    (leg: IJourneyLeg) =>
      !!leg.operator.id && leg.operator.id !== onDemandLeg.operator.id
  );
  const departureLeg: IJourneyLeg | undefined = regularLegs.find(
    (leg: IJourneyLeg) =>
      isSameStation(leg.origin, tariffSegment.startStationId)
  );
  const arrivalLeg: IJourneyLeg | undefined = [...regularLegs]
    .reverse()
    .find((leg: IJourneyLeg) =>
      isSameStation(leg.destination, tariffSegment.endStationId)
    );

  return departureLeg && arrivalLeg
    ? {
        departureLeg,
        origin: departureLeg.origin.locationReferenceId,
        destination: arrivalLeg.destination.locationReferenceId,
      }
    : undefined;
}

export function findMatchingTariffVia(
  vias: ITariffRelationVia[],
  viaDescription: string
): ITariffRelationVia | undefined {
  const describedViaPoints: Set<string> = new Set(
    splitAndNormalizeViaPoints(viaDescription)
  );

  return vias
    .map((via: ITariffRelationVia) => ({
      via,
      matchingPoints: splitAndNormalizeViaPoints(via.via).filter(
        (viaPoint: string) => describedViaPoints.has(viaPoint)
      ).length,
      totalPoints: splitAndNormalizeViaPoints(via.via).length,
    }))
    .filter(
      candidate =>
        candidate.totalPoints > 0 &&
        candidate.matchingPoints === candidate.totalPoints
    )
    .sort((a, b) => b.totalPoints - a.totalPoints)[0]?.via;
}

function isSameStation(location: IJourneyLocation, stationId: string): boolean {
  const normalizedStationId: string = normalizeStationReference(stationId);

  return [location.locationReferenceId, location.externalId].some(
    (reference: string) =>
      normalizeStationReference(reference) === normalizedStationId
  );
}

function normalizeStationReference(reference: string): string {
  const normalizedReference: string = reference.replace(/^A×/, '');
  const austrianStationReference: RegExpMatchArray | null =
    normalizedReference.match(/^(at:[^:]+:[^:]+)/);

  return austrianStationReference?.[1] ?? normalizedReference;
}

function splitAndNormalizeViaPoints(via: string): string[] {
  return via
    .split(',')
    .map((viaPoint: string) =>
      viaPoint
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase()
    )
    .filter(Boolean);
}
